import React, { useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const MOBILE = '@media (max-width: 800px)';

const useStyles = makeStyles(() => ({
    article: {
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        background: '#FFFFFF',
        overflow: 'hidden',
        transition: 'border-color 200ms ease',
    },
    articleOpen: {
        borderColor: '#1B2430',
    },
    headerButton: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px 18px',
        padding: '18px clamp(18px, 2.4vw, 28px)',
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        color: '#1B2430',
        outline: 'none',
        '&:focus': {
            outline: 'none',
        },
        '&:focus-visible': {
            outline: '2px solid #ED6628',
            outlineOffset: -2,
            borderRadius: 20,
        },
    },
    title: {
        fontSize: 'clamp(17px, 1.6vw, 21px)',
        fontWeight: 800,
        letterSpacing: '-0.015em',
        flex: '1 1 260px',
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 11px',
        borderRadius: 999,
        fontSize: '11.5px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    chipSoon: {
        background: '#F4F6F9',
        color: '#6B7280',
    },
    chipAvailable: {
        background: '#FDECE3',
        color: '#A63C0C',
    },
    mark: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid #D5D9E0',
        fontSize: 18,
        fontWeight: 600,
        color: '#ED6628',
        flex: 'none',
    },
    body: {
        padding: '0 clamp(18px, 2.4vw, 28px) clamp(20px, 2.4vw, 28px)',
        display: 'grid',
        gap: 22,
    },
    qdCard: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 20,
        [MOBILE]: {
            gridTemplateColumns: '1fr',
        },
    },
    qdQuestion: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '22px 24px',
        borderRadius: 14,
        background: '#E1F0F9',
        border: '1px solid #C9E2F2',
        boxSizing: 'border-box',
    },
    qdQuestionLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: '#1F5F86',
    },
    qdQuestionText: {
        margin: 0,
        fontSize: 17,
        fontWeight: 600,
        lineHeight: 1.4,
        color: '#1B2430',
    },
    qdDelivers: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '22px 24px',
        borderRadius: 14,
        background: '#F4F3F1',
        boxSizing: 'border-box',
    },
    qdDeliversLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: '#6B7280',
    },
    qdDeliversText: {
        margin: 0,
        fontSize: 15,
        lineHeight: 1.55,
        color: '#1B2430',
    },
    waitlistBtn: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        height: 46,
        padding: '0 20px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 14.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': {
            background: '#F2F4F7',
            borderColor: '#C4C9D2',
        },
    },
}));

const PbdqCosAssessmentRow = ({
    title,
    statusLabel,
    isComingSoon,
    question,
    does, // kept in props for interface compatibility
    delivers,
    isOpen,
    onToggle,
    onWaitlistClick,
    children,
}) => {
    const classes = useStyles();
    const rowRef = useRef(null);
    const wasOpenRef = useRef(isOpen);

    useEffect(() => {
        // If this row just transitioned from closed to open:
        if (!wasOpenRef.current && isOpen && rowRef.current) {
            // Give DOM time to update layout after previous large card collapses
            requestAnimationFrame(() => {
                const rect = rowRef.current?.getBoundingClientRect();
                if (rect) {
                    // Header offset so it doesn't get hidden behind any fixed top nav
                    const targetTop = window.scrollY + rect.top - 84;
                    window.scrollTo({
                        top: Math.max(0, targetTop),
                        behavior: 'smooth',
                    });
                }
            });
        }
        wasOpenRef.current = isOpen;
    }, [isOpen]);

    return (
        <article ref={rowRef} className={clsx(classes.article, isOpen && classes.articleOpen)}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                className={classes.headerButton}
            >
                <span className={classes.title}>{title}</span>
                <span
                    className={clsx(
                        classes.chip,
                        isComingSoon ? classes.chipSoon : classes.chipAvailable
                    )}
                >
                    {statusLabel}
                </span>
                <span aria-hidden="true" className={classes.mark}>
                    {isOpen ? '−' : '+'}
                </span>
            </button>

            {isOpen && (
                <div className={classes.body}>
                    {/* Two cards: Question it answers (blue) and Delivers (light grey) */}
                    <div className={classes.qdCard}>
                        <div className={classes.qdQuestion}>
                            <span className={classes.qdQuestionLabel}>The question it answers</span>
                            <p className={classes.qdQuestionText}>{question}</p>
                        </div>
                        <div className={classes.qdDelivers}>
                            <span className={classes.qdDeliversLabel}>Delivers</span>
                            <p className={classes.qdDeliversText}>{delivers}</p>
                        </div>
                    </div>

                    {isComingSoon && onWaitlistClick && (
                        <div>
                            <button
                                type="button"
                                onClick={onWaitlistClick}
                                className={classes.waitlistBtn}
                            >
                                Join the waitlist
                            </button>
                        </div>
                    )}

                    {children}
                </div>
            )}
        </article>
    );
};

export default PbdqCosAssessmentRow;
